import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles, Twitter, Copy, Check, Share2, Send } from 'lucide-react';
import { toast } from 'sonner';

const PUBLIC_BASE = process.env.REACT_APP_PUBLIC_URL || 'https://bet4peer.com';

function buildReferralLink(address) {
    if (!address) return `${PUBLIC_BASE}/airdrop`;
    return `${PUBLIC_BASE}/airdrop?ref=${address.toLowerCase()}`;
}

function buildTweet(questName, reward, totalB4P, multiplier, referralUrl) {
    // Pre-filled, optimised for 280 chars
    return (
        `🎯 Just unlocked "${questName}" on @bet4peer — earned +${reward} $B4P!\n\n` +
        `My current allocation: ${totalB4P.toLocaleString()} B4P (×${multiplier} multiplier)\n\n` +
        `Lion's Gate airdrop · 08.08.2026 · 888 · join my squad 👇\n${referralUrl}`
    );
}

export function ShareCompletionModal({ open, onOpenChange, quest, reward, totalB4P, multiplier, address }) {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const referralUrl = buildReferralLink(address);
    const questLabel = quest ? t(`airdrop.quest_${quest.id}`) : '';
    const tweetText = buildTweet(questLabel, reward, totalB4P, multiplier, referralUrl);
    const twitterIntent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
    const telegramIntent = `https://t.me/share/url?url=${encodeURIComponent(referralUrl)}&text=${encodeURIComponent(`I just earned +${reward} $B4P on @bet4peer 🚀 Join me before 08.08.2026:`)}`;

    // Confetti-like animation reset
    useEffect(() => {
        if (open) setCopied(false);
    }, [open]);

    function copyReferralLink() {
        navigator.clipboard.writeText(referralUrl);
        setCopied(true);
        toast.success(t('shareModal.linkCopied'));
        setTimeout(() => setCopied(false), 2000);
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#0A0A0C] border-[#FFD700]/30 max-w-md" data-testid="share-completion-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-white">
                        <Sparkles className="w-5 h-5 text-[#FFD700] animate-pulse" />
                        {t('shareModal.title')}
                    </DialogTitle>
                </DialogHeader>

                {/* Reward banner */}
                <div className="text-center py-4 px-4 rounded-xl bg-gradient-to-br from-[#FFD700]/15 to-[#FF6B35]/10 border border-[#FFD700]/30">
                    <div className="text-xs text-white/60 uppercase tracking-wider mb-1">{t('shareModal.earned')}</div>
                    <div className="font-mono text-4xl font-black text-[#FFD700] tabular-nums">+{reward.toLocaleString()} <span className="text-lg">B4P</span></div>
                    <div className="text-xs text-white/50 mt-2">
                        {t('shareModal.totalNow', { total: totalB4P.toLocaleString(), mult: multiplier })}
                    </div>
                </div>

                {/* Pre-filled tweet preview */}
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10">
                    <div className="text-xs text-white/40 uppercase tracking-wider mb-2">{t('shareModal.tweetPreview')}</div>
                    <p className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed font-mono" data-testid="share-tweet-preview">
                        {tweetText}
                    </p>
                </div>

                {/* CTAs */}
                <div className="grid grid-cols-2 gap-2">
                    <a
                        href={twitterIntent}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => toast.success(t('shareModal.openingTwitter'))}
                    >
                        <Button
                            className="w-full bg-[#1DA1F2] text-white hover:bg-[#1A8ED1] font-bold"
                            data-testid="share-twitter-btn"
                        >
                            <Twitter className="w-4 h-4 mr-2" />
                            X / Twitter
                        </Button>
                    </a>
                    <a href={telegramIntent} target="_blank" rel="noopener noreferrer">
                        <Button
                            className="w-full bg-[#0088CC] text-white hover:bg-[#0077B5] font-bold"
                            data-testid="share-telegram-btn"
                        >
                            <Send className="w-4 h-4 mr-2" />
                            Telegram
                        </Button>
                    </a>
                </div>

                {/* Copy ref link */}
                <Button
                    onClick={copyReferralLink}
                    variant="outline"
                    className="w-full border-white/20 text-white hover:bg-white/5"
                    data-testid="share-copy-btn"
                >
                    {copied ? (
                        <><Check className="w-4 h-4 mr-2 text-[#00FF66]" /> {t('shareModal.copied')}</>
                    ) : (
                        <><Copy className="w-4 h-4 mr-2" /> {t('shareModal.copyLink')}</>
                    )}
                </Button>

                <div className="text-center text-xs text-white/40 flex items-center justify-center gap-1">
                    <Share2 className="w-3 h-3" />
                    {t('shareModal.bonusNote')}
                </div>
            </DialogContent>
        </Dialog>
    );
}
